/**
 * HeroAssistantWizard — modal launched from the Hero character grid.
 *
 * Flow:
 *   1. User clicks a character (Zyntha / Thoro / Zyona / Zynthoro Assist)
 *   2. That assistant asks 3 short questions
 *   3. Modal shows the recommended plan + a direct CTA to checkout
 *
 * Keeps the "Modern Classic" aesthetic — restrained typography, generous
 * spacing, subtle shadow, brand accents.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, Check } from "lucide-react";

// Plans mirror the site-wide PricingCarousel so recommendations stay in sync.
const PLANS = {
  "site-basis": {
    name: "Site Basis",
    tagline: "A simple monthly site to get you online",
    price: "€9",
    unit: "/month",
    href: "/website?checkout=basis",
    cta: "Start Site Basis",
    accent: "#202B60",
  },
  "site-plus": {
    name: "Site Plus",
    tagline: "Growing sites with a custom domain",
    price: "€17",
    unit: "/month",
    href: "/website?checkout=plus",
    cta: "Start Site Plus",
    accent: "#202B60",
  },
  k1: {
    name: "Kickstart 1",
    tagline: "Lean lifetime access, one payment",
    price: "€79",
    unit: "· one-time",
    href: "/pricing#kickstart",
    cta: "See Kickstart 1",
    accent: "#D4AF37",
  },
  k2: {
    name: "Kickstart 2",
    tagline: "The founder favourite — best balance",
    price: "€149",
    unit: "· one-time",
    href: "/pricing#kickstart",
    cta: "See Kickstart 2",
    accent: "#202B60",
  },
  k3: {
    name: "Kickstart 3",
    tagline: "The complete lifetime business suite",
    price: "€199",
    unit: "· one-time",
    href: "/pricing#kickstart",
    cta: "See Kickstart 3",
    accent: "#D4AF37",
  },
  starter: {
    name: "Starter",
    tagline: "For solo founders getting started",
    price: "€499",
    unit: "/month",
    href: "/pricing#subscriptions",
    cta: "See Starter",
    accent: "#202B60",
  },
  creator: {
    name: "Creator",
    tagline: "Full AI creative suite",
    price: "€699",
    unit: "/month",
    href: "/pricing#subscriptions",
    cta: "See Creator",
    accent: "#202B60",
  },
  business: {
    name: "Business",
    tagline: "For growing SMEs",
    price: "€899",
    unit: "/month",
    href: "/pricing#subscriptions",
    cta: "See Business",
    accent: "#202B60",
  },
  agency: {
    name: "Agency",
    tagline: "For multi-client teams",
    price: "€1,199",
    unit: "/month",
    href: "/pricing#subscriptions",
    cta: "See Agency",
    accent: "#202B60",
  },
  enterprise: {
    name: "Enterprise",
    tagline: "All 12 domains · full ERP",
    price: "from €2,499",
    unit: "/month",
    href: "/pricing#enterprise",
    cta: "Talk to Sales",
    accent: "#D4AF37",
  },
};

const ASSISTANT_INTRO = {
  zyntha: {
    name: "Zyntha",
    role: "Content & SEO",
    line: "Three quick questions and I’ll match you to the plan that fits how you build.",
  },
  thoro: {
    name: "Thoro",
    role: "Workflows & Builder",
    line: "Give me thirty seconds — I’ll route you to the plan that scales with your workflows.",
  },
  zyona: {
    name: "Zyona",
    role: "Business & Growth",
    line: "Answer three questions and I’ll recommend the plan with the best ROI for your stage.",
  },
  assist: {
    name: "Zynthoro Assist",
    role: "Always-on guide",
    line: "Answer three quick questions and I’ll point you to the plan that fits you best.",
  },
};

const QUESTIONS = [
  {
    id: "team",
    label: "How large is your team today?",
    options: [
      { id: "solo", label: "Just me" },
      { id: "small", label: "2–5 people" },
      { id: "mid", label: "6–20 people" },
      { id: "large", label: "20+ people" },
    ],
  },
  {
    id: "need",
    label: "What do you need Zynthoro to do first?",
    options: [
      { id: "site", label: "Publish a professional website" },
      { id: "automate", label: "Automate workflows & operations" },
      { id: "growth", label: "Turn data into growth decisions" },
      { id: "erp", label: "Run the whole business end-to-end" },
    ],
  },
  {
    id: "pay",
    label: "How do you prefer to pay?",
    options: [
      { id: "monthly", label: "Monthly subscription" },
      { id: "lifetime", label: "One-time lifetime deal" },
    ],
  },
];

// Deterministic recommendation:
//  - Monthly subscribers always get a MONTHLY plan (Starter → Enterprise)
//  - Lifetime buyers always get a KICKSTART plan (K1 / K2 / K3)
//  - "Just publish a website" is its own dedicated path (Site Basis / Site Plus / K1)
function recommend(answers) {
  const { team, need, pay } = answers;

  // 1. Website-first path — Site plans (monthly) or the lean lifetime option
  if (need === "site") {
    if (pay === "lifetime") return "k1";
    return team === "solo" ? "site-basis" : "site-plus";
  }

  // 2. Compute a fit score from team size + need complexity
  const teamScore = { solo: 0, small: 1, mid: 2, large: 3 }[team] ?? 0;
  const needScore = { automate: 0, growth: 1, erp: 2 }[need] ?? 0;
  const score = teamScore + needScore;

  // 3. Lifetime buyers → Kickstart tiers
  if (pay === "lifetime") {
    if (score <= 1) return "k1";
    if (score <= 3) return "k2";
    return "k3";
  }

  // 4. Monthly buyers → Subscription tiers (never Kickstart)
  if (score <= 0) return "starter";
  if (score === 1) return "creator";
  if (score === 2) return "business";
  if (score === 3) return "agency";
  return "enterprise";
}

export default function HeroAssistantWizard({ assistant, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});

  // Reset whenever a new assistant opens the wizard.
  useEffect(() => {
    if (assistant) {
      setStep(0);
      setAnswers({});
    }
  }, [assistant?.key]);

  // Lock scroll + ESC to close.
  useEffect(() => {
    if (!assistant) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [assistant, onClose]);

  if (!assistant) return null;

  const intro = ASSISTANT_INTRO[assistant.key] || ASSISTANT_INTRO.assist;
  const totalSteps = QUESTIONS.length;
  const isResult = step >= totalSteps;
  const currentQ = !isResult ? QUESTIONS[step] : null;
  const recKey = isResult ? recommend(answers) : null;
  const rec = recKey ? PLANS[recKey] : null;

  const choose = (optionId) => {
    const next = { ...answers, [currentQ.id]: optionId };
    setAnswers(next);
    setStep((s) => s + 1);
  };

  return (
    <div
      data-testid="hero-wizard-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[#0A1628]/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        data-testid="hero-wizard-modal"
        className="relative w-full max-w-lg bg-white rounded-2xl shadow-[0_30px_80px_-30px_rgba(10,22,40,0.45)] border border-[#eef1f7] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          type="button"
          data-testid="hero-wizard-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center bg-white/90 border border-[#eef1f7] text-[#0A1628]/60 hover:text-[#0A1628] hover:bg-white transition-colors"
          aria-label="Close"
        >
          <X size={16} />
        </button>

        {/* Header with the assistant */}
        <div className="px-8 pt-8 pb-6 flex items-center gap-4 border-b border-[#eef1f7]">
          <img
            src={assistant.src}
            alt={`${assistant.name} assistant avatar`}
            className="w-14 h-14 rounded-xl object-cover object-top border border-[#eef1f7]"
          />
          <div className="min-w-0">
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.14em] text-[#202B60]">
              {intro.role}
            </p>
            <p className="text-[17px] font-semibold text-[#0A1628] leading-tight mt-0.5">
              {intro.name}
            </p>
          </div>
        </div>

        {/* Body */}
        <div className="px-8 py-8">
          {/* Progress */}
          {!isResult && (
            <div className="flex items-center gap-2 mb-6">
              {QUESTIONS.map((_, i) => (
                <span
                  key={i}
                  className="h-1 flex-1 rounded-full transition-colors"
                  style={{ background: i <= step ? "#202B60" : "#e6e9f0" }}
                />
              ))}
            </div>
          )}

          {!isResult ? (
            <>
              {step === 0 && (
                <p className="text-[14px] text-[#0A1628]/70 mb-5 leading-relaxed">
                  {intro.line}
                </p>
              )}

              <h3
                data-testid="hero-wizard-question"
                className="text-[20px] font-semibold text-[#0A1628] leading-snug"
              >
                {currentQ.label}
              </h3>

              <div className="mt-6 space-y-2.5">
                {currentQ.options.map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`hero-wizard-option-${currentQ.id}-${opt.id}`}
                    onClick={() => choose(opt.id)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-[#eef1f7] px-5 py-3.5 text-left text-[14.5px] font-medium text-[#0A1628] bg-white hover:border-[#202B60] hover:bg-[#f7f9ff] transition-colors"
                  >
                    <span>{opt.label}</span>
                    <ArrowRight size={15} className="text-[#0A1628]/40" />
                  </button>
                ))}
              </div>

              {step > 0 && (
                <button
                  type="button"
                  data-testid="hero-wizard-back"
                  onClick={() => setStep((s) => Math.max(0, s - 1))}
                  className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1628]/60 hover:text-[#0A1628]"
                >
                  <ArrowLeft size={13} /> Back
                </button>
              )}
            </>
          ) : (
            <div data-testid="hero-wizard-result">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#202B60]/10 text-[#202B60] px-3 py-1 text-[11.5px] font-semibold uppercase tracking-[0.14em]">
                <Check size={12} /> Recommended for you
              </span>
              <h3 className="mt-4 text-[26px] font-bold text-[#0A1628] leading-tight">
                {rec.name}
              </h3>
              <p className="mt-2 text-[14.5px] text-[#0A1628]/70">{rec.tagline}</p>

              <div className="mt-5 flex items-baseline gap-1.5">
                <span className="text-[30px] font-bold tracking-tight text-[#0A1628]">
                  {rec.price}
                </span>
                <span className="text-[13.5px] text-[#0A1628]/60">{rec.unit}</span>
              </div>

              <Link
                to={rec.href}
                data-testid="hero-wizard-cta"
                onClick={onClose}
                className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-[14.5px] font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: rec.accent }}
              >
                {rec.cta} <ArrowRight size={15} />
              </Link>

              <button
                type="button"
                data-testid="hero-wizard-restart"
                onClick={() => {
                  setStep(0);
                  setAnswers({});
                }}
                className="mt-4 inline-flex items-center gap-1.5 text-[13px] font-medium text-[#0A1628]/60 hover:text-[#0A1628]"
              >
                <ArrowLeft size={13} /> Start over
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
